import React, { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/ui-blocks/PageHeader";
import { api, endpoints, adminAuth } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSite as useSiteCtx } from "@/lib/site";
import { Lock, LogOut, Pencil, Trash2, Plus, ShieldCheck, Save, Image as ImageIcon } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "");

/* ---------------- Login ---------------- */
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(endpoints.login, { email, password });
      adminAuth.set(res.access_token);
      toast.success(`Welcome back, ${res.name || "Admin"}`);
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="container-x py-20 max-w-md">
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-8 caf-shadow">
        <div className="h-12 w-12 rounded-xl bg-[hsl(var(--surface-tint))] grid place-items-center text-[hsl(var(--green))]"><Lock className="h-6 w-6" /></div>
        <h1 className="heading-3 mt-4">Admin sign in</h1>
        <p className="text-sm text-[hsl(var(--muted-ink))] mt-1">Secure access for the CAF team.</p>
        <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="admin-login-form">
          <div>
            <Label className="text-sm">Email</Label>
            <Input required type="email" data-testid="admin-login-email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="caf@admin.com" />
          </div>
          <div>
            <Label className="text-sm">Password</Label>
            <Input required type="password" data-testid="admin-login-password" className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button disabled={loading} type="submit" data-testid="admin-login-submit" className="bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white font-semibold">{loading ? "Signing in…" : "Sign in"}</Button>
        </form>
      </div>
    </div>
  );
};

/* ---------------- Submissions tables ---------------- */
const TabTable = ({ path, columns, testid }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get(path).then((r) => { setRows(Array.isArray(r) ? r : []); setLoading(false); }).catch(() => setLoading(false)); }, [path]);
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-card overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-[hsl(var(--surface-tint))]">
            <TableRow>{columns.map((c) => (<TableHead key={c.key} className="text-[hsl(var(--navy))]">{c.label}</TableHead>))}</TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-[hsl(var(--muted-ink))]">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-[hsl(var(--muted-ink))]">No records yet.</TableCell></TableRow>
            ) : rows.map((r, idx) => (
              <TableRow key={r.id || idx}>{columns.map((c) => (<TableCell key={c.key}>{c.render ? c.render(r) : String(r[c.key] ?? "")}</TableCell>))}</TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="px-4 py-2 text-xs text-[hsl(var(--muted-ink))] border-t border-[hsl(var(--border))]">{rows.length} record(s)</div>
    </div>
  );
};

const SubmissionsPanel = () => (
  <Tabs defaultValue="donations">
    <TabsList className="bg-[hsl(var(--surface-tint))] p-1 flex-wrap h-auto">
      <TabsTrigger data-testid="admin-tab-donations" value="donations">Donations</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-volunteers" value="volunteers">Volunteers</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-issues" value="issues">Issue reports</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-newsletter" value="newsletter">Newsletter</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-contacts" value="contacts">Contacts</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-csr" value="csr">CSR</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-memberships" value="memberships">Memberships</TabsTrigger>
      <TabsTrigger data-testid="admin-tab-grievances" value="grievances">Grievances</TabsTrigger>
    </TabsList>
    <div className="mt-4">
      <TabsContent value="donations"><TabTable testid="admin-table-donations" path={endpoints.donations} columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"full_name",label:"Name"},{key:"amount",label:"Amount",render:(r)=>`₹${Number(r.amount).toLocaleString("en-IN")}`},{key:"method",label:"Method"},{key:"status",label:"Status",render:(r)=>(<Badge className={r.status==="paid"?"badge-green border":"badge-navy border"}>{r.status}</Badge>)},{key:"receipt_no",label:"Receipt",render:(r)=>r.receipt_no||"—"}]} /></TabsContent>
      <TabsContent value="volunteers"><TabTable testid="admin-table-volunteers" path={endpoints.volunteers} columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"full_name",label:"Name"},{key:"email",label:"Email"},{key:"phone",label:"Phone"},{key:"city",label:"City"},{key:"interests",label:"Interests",render:(r)=>(r.interests||[]).join(", ")}]} /></TabsContent>
      <TabsContent value="issues"><TabTable testid="admin-table-issues" path={endpoints.issues} columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"full_name",label:"Name"},{key:"location",label:"Location"},{key:"category",label:"Category"},{key:"has_photo",label:"Photo",render:(r)=>r.has_photo?"Yes":"No"},{key:"status",label:"Status"}]} /></TabsContent>
      <TabsContent value="newsletter"><TabTable testid="admin-table-newsletter" path={endpoints.newsletter} columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"email",label:"Email"}]} /></TabsContent>
      <TabsContent value="contacts"><TabTable testid="admin-table-contacts" path={endpoints.contact} columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"full_name",label:"Name"},{key:"email",label:"Email"},{key:"subject",label:"Subject"}]} /></TabsContent>
      <TabsContent value="csr"><TabTable testid="admin-table-csr" path={endpoints.csr} columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"company_name",label:"Company"},{key:"contact_person",label:"Contact"},{key:"email",label:"Email"}]} /></TabsContent>
      <TabsContent value="memberships"><TabTable testid="admin-table-memberships" path="/memberships" columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"member_id",label:"Member ID",render:(r)=>(<span className="font-bold text-[hsl(var(--orange))]">{r.member_id}</span>)},{key:"full_name",label:"Name"},{key:"email",label:"Email"},{key:"phone",label:"Phone"},{key:"city",label:"City"},{key:"membership_type",label:"Type"},{key:"status",label:"Status"}]} /></TabsContent>
      <TabsContent value="grievances"><TabTable testid="admin-table-grievances" path="/grievances" columns={[{key:"created_at",label:"Date",render:(r)=>fmt(r.created_at)},{key:"full_name",label:"Name"},{key:"subject",label:"Subject"},{key:"status",label:"Status"}]} /></TabsContent>
    </div>
  </Tabs>
);

/* ---------------- Content editor (campaigns / posts) ---------------- */
const FIELDS = {
  campaigns: [
    { key: "title", label: "Title" }, { key: "summary", label: "Summary", textarea: true },
    { key: "category", label: "Category" }, { key: "location", label: "Location" },
    { key: "status", label: "Status (active/upcoming/completed)" },
    { key: "goal_inr", label: "Goal (INR)", number: true }, { key: "raised_inr", label: "Raised (INR)", number: true },
    { key: "image", label: "Image URL" },
    { key: "start_date", label: "Start date (YYYY-MM-DD)" },
  ],
  posts: [
    { key: "title", label: "Title" }, { key: "excerpt", label: "Excerpt", textarea: true },
    { key: "category", label: "Category" }, { key: "author", label: "Author" },
    { key: "cover_image", label: "Cover image URL" }, { key: "read_min", label: "Read minutes", number: true },
  ],
};

const ContentEditor = ({ kind }) => {
  const listPath = kind === "campaigns" ? endpoints.campaigns : endpoints.posts;
  const adminPath = kind === "campaigns" ? endpoints.adminCampaigns : endpoints.adminPosts;
  const fields = FIELDS[kind];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new, doc for edit
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => { api.get(listPath).then((r) => setItems(Array.isArray(r) ? r : [])).catch(() => {}); }, [listPath]);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      fields.forEach(({ key, number }) => {
        const v = editing[key];
        if (v !== undefined && v !== "") body[key] = number ? Number(v) : v;
      });
      if (editing.id) await api.put(`${adminPath}/${editing.id}`, body);
      else await api.post(adminPath, body);
      toast.success("Saved successfully");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try { await api.del(`${adminPath}/${id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[hsl(var(--muted-ink))]">{items.length} {kind}</div>
        <Button size="sm" data-testid={`admin-${kind}-new`} onClick={() => setEditing({})} className="bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Plus className="h-4 w-4 mr-1" /> New</Button>
      </div>
      <div className="rounded-xl border border-[hsl(var(--border))] bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-[hsl(var(--surface-tint))]">
              <TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>{kind === "campaigns" ? "Raised / Goal" : "Author"}</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`admin-${kind}-row-${it.slug || it.id}`}>
                  <TableCell className="font-medium max-w-[280px] truncate">{it.title}</TableCell>
                  <TableCell>{it.category}</TableCell>
                  <TableCell>{kind === "campaigns" ? `₹${Number(it.raised_inr || 0).toLocaleString("en-IN")} / ₹${Number(it.goal_inr || 0).toLocaleString("en-IN")}` : it.author}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`admin-${kind}-edit-${it.slug || it.id}`} onClick={() => setEditing({ ...it })}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" data-testid={`admin-${kind}-delete-${it.slug || it.id}`} onClick={() => remove(it.id)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid={`admin-${kind}-dialog`}>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Create"} {kind === "campaigns" ? "campaign" : "post"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {fields.map(({ key, label, textarea, number }) => (
              <div key={key}>
                <Label className="text-xs">{label}</Label>
                {textarea ? (
                  <Textarea data-testid={`admin-field-${key}`} rows={3} className="mt-1" value={editing?.[key] ?? ""} onChange={(e) => setEditing((s) => ({ ...s, [key]: e.target.value }))} />
                ) : (
                  <Input data-testid={`admin-field-${key}`} type={number ? "number" : "text"} className="mt-1" value={editing?.[key] ?? ""} onChange={(e) => setEditing((s) => ({ ...s, [key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={saving} data-testid={`admin-${kind}-save`} onClick={save} className="bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------- Site settings (logo, contact, hero copy, socials, media) ---------------- */
const SITE_FIELD_GROUPS = [
  { group: "Branding", fields: [
    { key: "org_name", label: "Organisation name (header, footer, receipts)" },
    { key: "tagline", label: "Logo tagline (small line under name)" },
    { key: "logo_url", label: "Logo image URL (empty = default uploaded logo)" },
  ]},
  { group: "Hero section", fields: [
    { key: "hero_headline", label: "Hero headline (first part)" },
    { key: "hero_highlight", label: "Hero highlighted word" },
    { key: "hero_sub", label: "Hero sub-text", textarea: true },
    { key: "hero_image_1", label: "Hero main image URL" },
    { key: "hero_image_2", label: "Hero small image 1 URL" },
    { key: "hero_image_3", label: "Hero small image 2 URL" },
  ]},
  { group: "Contact", fields: [
    { key: "contact_email", label: "Contact email" },
    { key: "contact_phone", label: "Contact phone" },
    { key: "address", label: "Address", textarea: true },
  ]},
  { group: "Social media (leave empty to hide icon)", fields: [
    { key: "facebook_url", label: "Facebook URL" },
    { key: "twitter_url", label: "X / Twitter URL" },
    { key: "instagram_url", label: "Instagram URL" },
    { key: "linkedin_url", label: "LinkedIn URL" },
    { key: "youtube_url", label: "YouTube URL" },
    { key: "whatsapp_url", label: "WhatsApp link (wa.me/...)" },
    { key: "telegram_url", label: "Telegram URL" },
  ]},
  { group: "Homepage video", fields: [
    { key: "video_embed_url", label: "Video embed URL (YouTube embed link)" },
    { key: "video_poster_url", label: "Video poster image URL" },
    { key: "video_caption", label: "Video caption" },
  ]},
  { group: "Impact section (annotated image on homepage)", fields: [
    { key: "impact_image_url", label: "Impact image URL (use Indian community photos)" },
    { key: "impact_title", label: "Impact headline", textarea: true },
    { key: "impact_sub", label: "Impact sub-text" },
    { key: "impact_callout_1", label: "Callout 1 (saffron dot)" },
    { key: "impact_callout_2", label: "Callout 2 (white dot)" },
    { key: "impact_callout_3", label: "Callout 3 (green dot)" },
  ]},
  { group: "Donate section", fields: [
    { key: "donate_image_url", label: "Donate section image URL" },
  ]},
  { group: "Footer", fields: [
    { key: "footer_about", label: "Footer about text", textarea: true },
    { key: "registered_line", label: "Registration line (footer)" },
  ]},
];

const SiteEditor = () => {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refreshSite } = useSiteCtx();
  useEffect(() => { api.get(endpoints.siteSettings).then(setSite).catch(() => {}); }, []);
  if (!site) return <div className="text-sm text-[hsl(var(--muted-ink))]">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const { id, updated_at, updated_by, ...body } = site;
      await api.put(endpoints.siteSettings, body);
      refreshSite();
      toast.success("Site settings saved — changes are live across the site");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-site-panel">
      {SITE_FIELD_GROUPS.map(({ group, fields }) => (
        <div key={group} className="rounded-xl border border-[hsl(var(--border))] bg-card p-6 mb-4">
          <div className="eyebrow mb-3">{group}</div>
          <div className="grid gap-4">
            {fields.map(({ key, label, textarea }) => (
              <div key={key}>
                <Label className="text-xs">{label}</Label>
                {textarea ? (
                  <Textarea data-testid={`admin-site-${key}`} rows={3} className="mt-1" value={site[key] ?? ""} onChange={(e) => setSite((s) => ({ ...s, [key]: e.target.value }))} />
                ) : (
                  <Input data-testid={`admin-site-${key}`} className="mt-1" value={site[key] ?? ""} onChange={(e) => setSite((s) => ({ ...s, [key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button disabled={saving} data-testid="admin-site-save" onClick={save} className="mt-1 bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save site settings"}</Button>
    </div>
  );
};

/* ---------------- Media & content manager (all site images/videos/text) ---------------- */
const MEDIA_COLLECTIONS = [
  { value: "gallery", label: "Gallery images" },
  { value: "programs", label: "Programs" },
  { value: "team", label: "Team members" },
  { value: "testimonials", label: "Testimonials" },
  { value: "success_stories", label: "Success stories" },
  { value: "events", label: "Events" },
  { value: "press_releases", label: "Press releases" },
  { value: "videos", label: "Videos" },
  { value: "awareness_modules", label: "Awareness modules" },
];

const IMAGE_KEYS = ["image", "cover_image", "photo", "thumb", "poster", "avatar"];
const TITLE_KEYS = ["title", "name", "caption", "quote"];

const MediaEditor = () => {
  const [collection, setCollection] = useState("gallery");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    api.get(`/admin/content/${collection}`).then((r) => setItems(Array.isArray(r) ? r : [])).catch(() => setItems([]));
  }, [collection]);
  useEffect(() => { reload(); }, [reload]);

  const editableKeys = (item) => Object.keys(item).filter((k) => !["id", "created_at", "updated_at"].includes(k) && (typeof item[k] === "string" || typeof item[k] === "number"));
  const imgOf = (item) => IMAGE_KEYS.map((k) => item[k]).find((v) => typeof v === "string" && v.startsWith("http"));
  const titleOf = (item) => TITLE_KEYS.map((k) => item[k]).find((v) => typeof v === "string") || item.id?.slice(0, 8);

  const save = async () => {
    setSaving(true);
    try {
      const { id, created_at, updated_at, ...body } = editing;
      if (id) await api.put(`/admin/content/${collection}/${id}`, body);
      else await api.post(`/admin/content/${collection}`, body);
      toast.success("Saved — refresh the page to see it live");
      setEditing(null);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item permanently from the database?")) return;
    try { await api.del(`/admin/content/${collection}/${id}`); toast.success("Deleted from database"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-media-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="w-64">
          <Select value={collection} onValueChange={setCollection}>
            <SelectTrigger data-testid="admin-media-collection"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MEDIA_COLLECTIONS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" data-testid="admin-media-new" onClick={() => setEditing({ title: "", image: "" })} className="bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Plus className="h-4 w-4 mr-1" /> New item</Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-ink))]">No items in this collection yet. Click "New item" to add one.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-media-grid">
          {items.map((item) => (
            <div key={item.id} className="rounded-xl border border-[hsl(var(--border))] bg-card overflow-hidden">
              {imgOf(item) ? (
                <img src={imgOf(item)} alt={titleOf(item)} className="h-32 w-full object-cover" loading="lazy" />
              ) : (
                <div className="h-32 grid place-items-center bg-[hsl(var(--surface-tint))] text-[hsl(var(--muted-ink))]"><ImageIcon className="h-8 w-8" /></div>
              )}
              <div className="p-3">
                <div className="text-sm font-semibold text-[hsl(var(--navy))] truncate">{titleOf(item)}</div>
                <div className="mt-2 flex gap-1.5">
                  <Button size="sm" variant="outline" data-testid={`admin-media-edit-${item.id?.slice(0,8)}`} onClick={() => setEditing({ ...item })}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(item.id)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="admin-media-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Create"} item — {MEDIA_COLLECTIONS.find((c) => c.value === collection)?.label}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-3">
              {editableKeys(editing).map((key) => (
                <div key={key}>
                  <Label className="text-xs capitalize">{key.replace(/_/g, " ")}</Label>
                  {String(editing[key]).length > 80 ? (
                    <Textarea data-testid={`admin-media-field-${key}`} rows={3} className="mt-1" value={editing[key] ?? ""} onChange={(e) => setEditing((s) => ({ ...s, [key]: e.target.value }))} />
                  ) : (
                    <Input data-testid={`admin-media-field-${key}`} className="mt-1" value={editing[key] ?? ""} onChange={(e) => setEditing((s) => ({ ...s, [key]: e.target.value }))} />
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={saving} data-testid="admin-media-save" onClick={save} className="bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------- Live impact stats editor ---------------- */
const STAT_FIELDS = [
  { key: "lives_supported", label: "Lives supported" },
  { key: "volunteers", label: "Volunteers" },
  { key: "campaigns", label: "Campaigns run" },
  { key: "villages_reached", label: "Villages reached" },
  { key: "trees_planted", label: "Trees planted" },
  { key: "medical_camps", label: "Medical camps" },
];

const StatsEditor = () => {
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(endpoints.stats).then(setStats).catch(() => {}); }, []);
  if (!stats) return null;

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      STAT_FIELDS.forEach(({ key }) => { body[key] = Number(stats[key]) || 0; });
      await api.put("/admin/stats", body);
      toast.success("Impact numbers updated — live across the site");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl rounded-xl border border-[hsl(var(--border))] bg-card p-6 mt-4" data-testid="admin-stats-panel">
      <div className="eyebrow mb-3">Live impact numbers (homepage counters)</div>
      <div className="grid gap-4 sm:grid-cols-3">
        {STAT_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <Label className="text-xs">{label}</Label>
            <Input type="number" min="0" data-testid={`admin-stat-${key}`} className="mt-1" value={stats[key] ?? 0} onChange={(e) => setStats((s) => ({ ...s, [key]: e.target.value }))} />
          </div>
        ))}
      </div>
      <Button disabled={saving} data-testid="admin-stats-save" onClick={save} className="mt-5 bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save impact numbers"}</Button>
    </div>
  );
};

/* ---------------- SEO settings ---------------- */
const SeoEditor = () => {
  const [seo, setSeo] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(endpoints.seoSettings).then(setSeo).catch(() => {}); }, []);
  if (!seo) return <div className="text-sm text-[hsl(var(--muted-ink))]">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const { id, updated_at, updated_by, ...body } = seo;
      await api.put(endpoints.seoSettings, body);
      toast.success("SEO settings saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const fields = [
    { key: "site_title", label: "Site title (browser tab + Open Graph)" },
    { key: "site_description", label: "Meta description", textarea: true },
    { key: "keywords", label: "Keywords (comma separated)" },
    { key: "og_image", label: "Open Graph image URL" },
    { key: "twitter_handle", label: "Twitter/X handle" },
  ];

  return (
    <div className="max-w-2xl rounded-xl border border-[hsl(var(--border))] bg-card p-6" data-testid="admin-seo-panel">
      <div className="grid gap-4">
        {fields.map(({ key, label, textarea }) => (
          <div key={key}>
            <Label className="text-xs">{label}</Label>
            {textarea ? (
              <Textarea data-testid={`admin-seo-${key}`} rows={3} className="mt-1" value={seo[key] ?? ""} onChange={(e) => setSeo((s) => ({ ...s, [key]: e.target.value }))} />
            ) : (
              <Input data-testid={`admin-seo-${key}`} className="mt-1" value={seo[key] ?? ""} onChange={(e) => setSeo((s) => ({ ...s, [key]: e.target.value }))} />
            )}
          </div>
        ))}
      </div>
      <Button disabled={saving} data-testid="admin-seo-save" onClick={save} className="mt-5 bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save SEO settings"}</Button>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function AdminPage() {
  const [authed, setAuthed] = useState(adminAuth.isLoggedIn());

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return;
    api.get(endpoints.me).catch(() => { adminAuth.clear(); setAuthed(false); });
  }, []);

  if (!authed) {
    return (
      <main>
        <PageHeader eyebrow="Admin" title="Team sign in" lead="Manage campaigns, posts, SEO and view submissions — secured with JWT." />
        <AdminLogin onLogin={() => setAuthed(true)} />
      </main>
    );
  }

  return (
    <main>
      <PageHeader eyebrow="Admin" title="CAF Control Room" lead="Live submissions, content management and SEO — all in one place." />
      <section className="container-wide py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="inline-flex items-center gap-2 text-sm text-[hsl(var(--green))] font-semibold"><ShieldCheck className="h-4 w-4" /> Signed in as caf@admin.com</div>
          <Button variant="outline" size="sm" data-testid="admin-logout" onClick={() => { adminAuth.clear(); setAuthed(false); toast.info("Signed out"); }}><LogOut className="h-4 w-4 mr-1.5" /> Sign out</Button>
        </div>
        <Tabs defaultValue="submissions">
          <TabsList className="bg-[hsl(var(--surface-tint))] p-1">
            <TabsTrigger data-testid="admin-main-tab-submissions" value="submissions">Submissions</TabsTrigger>
            <TabsTrigger data-testid="admin-main-tab-campaigns" value="campaigns">Campaigns</TabsTrigger>
            <TabsTrigger data-testid="admin-main-tab-posts" value="posts">Posts</TabsTrigger>
            <TabsTrigger data-testid="admin-main-tab-media" value="media">Media</TabsTrigger>
            <TabsTrigger data-testid="admin-main-tab-site" value="site">Site</TabsTrigger>
            <TabsTrigger data-testid="admin-main-tab-seo" value="seo">SEO</TabsTrigger>
          </TabsList>
          <div className="mt-5">
            <TabsContent value="submissions"><SubmissionsPanel /></TabsContent>
            <TabsContent value="campaigns"><ContentEditor kind="campaigns" /></TabsContent>
            <TabsContent value="posts"><ContentEditor kind="posts" /></TabsContent>
            <TabsContent value="media"><MediaEditor /></TabsContent>
            <TabsContent value="site"><SiteEditor /><StatsEditor /></TabsContent>
            <TabsContent value="seo"><SeoEditor /></TabsContent>
          </div>
        </Tabs>
      </section>
    </main>
  );
}
